// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect, lazy, Suspense } from 'react';
import recreationManager from '../utils/game/RecreationManager';
import crewManager from '../utils/game/CrewManager';
import './RecreationPanel.css';

// Lazy-load — only fetched when the player opens Poker Night.
const PokerMiniGame = lazy(() => import('./PokerMiniGame'));

const RecreationPanel = () => {
  const [facilities, setFacilities] = useState([]);
  const [activeActivities, setActiveActivities] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');
  const [showPokerGame, setShowPokerGame] = useState(false);

  useEffect(() => {
    updateData();
    const interval = setInterval(updateData, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const updateData = () => {
    setFacilities(recreationManager.getInstalledFacilities());
    setActiveActivities(recreationManager.getActiveActivities());
    
    // Process completed activities
    const completed = recreationManager.processActivities();
    completed.forEach(result => {
      if (result.moraleChange) {
        crewManager.adjustMorale(result.moraleChange);
      }
    });
  };

  const handleStartActivity = () => {
    if (!selectedCrew || !selectedActivity) return;
    
    const result = recreationManager.startActivity(selectedCrew, selectedActivity);
    if (result.success) {
      updateData();
      setSelectedCrew('');
      setSelectedActivity('');
    }
  };

  const availableActivities = selectedCrew ? 
    recreationManager.getAvailableActivities(selectedCrew) : [];
  
  const crew = crewManager.getAllCrew();
  const bonuses = recreationManager.getFacilityBonuses();

  return (
    <div className="recreation-panel">
      <div className="panel-header">
        <h3>🎮 Recreation Facilities</h3>
        <div className="bonuses">
          Morale Bonus: +{bonuses.moraleBonus} | Relationship Bonus: +{bonuses.relationshipBonus}
        </div>
      </div>

      {/* Installed Facilities */}
      <div className="facilities-section">
        <h4>Installed Facilities</h4>
        {facilities.length === 0 ? (
          <div className="no-facilities">No recreation facilities installed</div>
        ) : (
          <div className="facilities-grid">
            {facilities.map(facility => (
              <div key={facility.id} className="facility-card">
                <span className="facility-icon">{facility.icon}</span>
                <div className="facility-info">
                  <div className="facility-name">{facility.name}</div>
                  <div className="facility-bonus">+{facility.moraleBonus} Morale</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Start Activity */}
      {facilities.length > 0 && (
        <div className="activity-section">
          <h4>Start Recreation Activity</h4>
          <div className="activity-controls">
            <select 
              value={selectedCrew} 
              onChange={(e) => setSelectedCrew(e.target.value)}
              className="crew-select"
            >
              <option value="">Select Crew Member</option>
              {crew.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name} ({member.roleData.name})
                </option>
              ))}
            </select>

            <select 
              value={selectedActivity} 
              onChange={(e) => setSelectedActivity(e.target.value)}
              className="activity-select"
              disabled={!selectedCrew}
            >
              <option value="">Select Activity</option>
              {availableActivities.map(activity => (
                <option key={activity.id} value={activity.id}>
                  {activity.name} ({activity.duration}min)
                </option>
              ))}
            </select>

            <button 
              onClick={handleStartActivity}
              disabled={!selectedCrew || !selectedActivity}
              className="start-button"
            >
              Start Activity
            </button>
          </div>
        </div>
      )}

      {/* Mini-Games Section */}
      <div className="minigames-section">
        <h4>🎲 Mini-Games</h4>
        <div className="minigame-buttons">
          <button 
            onClick={() => setShowPokerGame(true)}
            className="poker-button"
            disabled={crew.length < 3}
          >
            🃏 Poker Night
            {crew.length < 3 && <span className="disabled-text">(Need 3+ crew)</span>}
          </button>
        </div>
      </div>

      {/* Active Activities */}
      {activeActivities.length > 0 && (
        <div className="active-section">
          <h4>Active Activities</h4>
          <div className="activities-list">
            {activeActivities.map(activity => {
              const crewMember = crew.find(c => c.id === activity.crewId);
              const timeLeft = Math.max(0, (activity.startTime + activity.duration - Date.now()) / 1000 / 60);
              
              return (
                <div key={activity.id} className="activity-item">
                  <span className="crew-name">{crewMember?.name}</span>
                  <span className="activity-name">{activity.activityId}</span>
                  <span className="time-left">{Math.ceil(timeLeft)}min left</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        {showPokerGame && (
          <PokerMiniGame 
            isOpen={showPokerGame}
            onClose={() => setShowPokerGame(false)}
            crewMembers={crew.slice(0, 3)}
          />
        )}
      </Suspense>
    </div>
  );
};

export default RecreationPanel;